import { DragEvent } from '@interactjs/types';
import { createPopper } from '@popperjs/core';
import * as bootstrap from 'bootstrap';
import interact from 'interactjs';
import { DateTime } from 'luxon';
import { Start, Stop } from 'smg/buttons';
import * as quoteproofs from 'smg/customer-api/quoteproofs';
import { CreateRequest, DeleteRequest, RestoreRequest } from 'smg/customer-api/revisions';
import { HandleRequestError } from 'smg/errors';
import { ReportValidity } from 'smg/form';
import * as models from 'smg/models';
import { G } from 'smg/querystring';
import * as template from 'smg/template';
import * as toast from 'smg/toast';
import { Uploader, UploadOptions } from 'smg/uploads';
import { RemToPx } from 'smg/util';
import { UUID } from 'smg/uuid';
import { PromptProofForRevision, QuoteProofForRevision } from '../components/revisions';
import { Relref } from '../hugo';
import { ModalOrOffcanvas } from '../modal';

interface myWindow extends Window {
    readonly cloudFront: string;
}
declare const window: myWindow;

if (document.body.id === 'revisions') {
    void main();
}

async function main() {
    const $spinner = document.querySelector('#revisions-spinner-container') as HTMLElement;

    const $notFound = document.querySelector('#revisions-not-found-container') as HTMLElement;
    const showNotFound = () => {
        $notFound.classList.remove('d-none');
        $spinner.remove();
    };

    const quoteHash = G().Get('id');
    if (!quoteHash) {
        showNotFound();
        return;
    }

    const resp = await quoteproofs.GetQuoteProofs().catch(err => {
        HandleRequestError(err);
        showNotFound();
        throw err;
    });
    const { quoteProofs } = resp.data;

    if (!quoteProofs.length) {
        showNotFound();
        return;
    }

    const quoteProofID = G().Get('proof');
    const quoteProof = quoteProofs.find(qp => qp.id === quoteProofID);

    if (!quoteProof) {
        const quoteProofsForRevision: QuoteProofForRevision[] = [];

        for (const quoteProof of quoteProofs || []) {
            quoteProofsForRevision.push({
                quoteProof,
            });
        }

        const quoteProofID = await PromptProofForRevision(quoteProofsForRevision, {
            closable: false,
        });
        if (!quoteProofID) {
            return;
        }
        window.location.href = `${Relref('/revisions')}?id=${quoteHash}&proof=${quoteProofID}`;

        return;
    }

    const $spotCommentHelpTemplate = document.querySelector('#revisions-spot-comments-help-template') as HTMLTemplateElement;

    const showSpotCommentHelp = () => {
        new ModalOrOffcanvas({
            body: template.Clone($spotCommentHelpTemplate).firstElementChild as HTMLElement,
            width: '40rem',
            appendConfirmButtonClass: 'w-100 w-sm-auto btn-sm-lg mx-auto',
            confirmText: 'Yes, I Understand',
            cancelButton: false,
            onSubmit: () => {
                window.localStorage.setItem('spot-comment-help-shown', 'true');
            },
            escape: false,
            appendClassBySelector: {
                '.modal-body': 'px-12',
                '.modal-footer': 'px-4 pt-6 pb-8',
                '.modal-title': 'fs-2 fw-900',
                '.modal-header': 'pb-4',

                '.offcanvas-body': 'px-4',
                '.offcanvas-footer': 'px-4 pt-6 pb-8',
                '.offcanvas-title': 'fs-4 fw-900',
            },
        });
    };

    if (window.localStorage.getItem('spot-comment-help-shown') !== 'true') {
        showSpotCommentHelp();
    }

    const $container = document.querySelector('#revisions-container') as HTMLElement;

    $spinner.remove();
    $container.classList.remove('d-none');

    const $proof = document.querySelector('#revisions-proof') as HTMLImageElement;
    const $showSpotCommentHelp = document.querySelector('#revisions-show-spot-comment-help') as HTMLAnchorElement;

    const $form = document.querySelector('#revisions-form') as HTMLFormElement;
    const $fieldset = $form.querySelector('fieldset') as HTMLFieldSetElement;
    const $submit = $form.querySelector('#revisions-submit') as HTMLButtonElement;

    const $comment = $form.querySelector('#revisions-comment') as HTMLTextAreaElement;

    const $attachmentsLabel = $form.querySelector('#revisions-attachments-label') as HTMLLabelElement;
    const $thumbnails = $form.querySelector('#revisions-thumbnails') as HTMLElement;
    const $thumbTemplate = document.querySelector('#revisions-thumbnail-template') as HTMLTemplateElement;

    const proofReady = new Promise<void>(resolve => {
        $proof.addEventListener('load', () => resolve());
    });
    $proof.src = `${window.cloudFront}/${quoteProof.proof?.file || ''}`;

    $showSpotCommentHelp.addEventListener('click', e => {
        e.preventDefault();
        showSpotCommentHelp();
    });

    const $deleteConfirmTemplate = document.querySelector('.uploader-thumbnail-delete-confirm-template') as HTMLTemplateElement;

    const thumbnailDeleteHandler: UploadOptions['thumbnailDeleteHandler'] = ($thumb, deleteFn) => {
        const t = new bootstrap.Tooltip($thumb, {
            trigger: 'manual',
            placement: 'top',
            title: 'Are you sure you want to delete this image?',
            html: true,
            offset: [0, 16],
        });

        const $deleteConfirm = template.Clone($deleteConfirmTemplate).firstElementChild as HTMLElement;

        const $confirm = $deleteConfirm.querySelector('.uploader-thumbnail-delete-confirm-button') as HTMLButtonElement;
        $confirm.addEventListener('click', () => {
            deleteFn();
            t.dispose();
        });

        const $cancel = $deleteConfirm.querySelector('.uploader-thumbnail-delete-cancel-button') as HTMLButtonElement;
        $cancel.addEventListener('click', () => t.dispose());

        t.setContent({ '.tooltip-inner': $deleteConfirm });
        t.show();
    };

    const u = new Uploader($attachmentsLabel, {
        thumbnails: $thumbnails,
        thumbnailTemplate: $thumbTemplate,
        disableLabelSpinner: true,
        thumbnailDeleteHandler,
        onidschange: () => {
            updateSubmitDisabled();
        },
    });

    const updateSubmitDisabled = () => {
        $submit.disabled = !$comment.value.trim() && !u.GetUploadIDs().length;
    };
    updateSubmitDisabled();

    $comment.addEventListener('input', updateSubmitDisabled);
    $comment.addEventListener('keydown', e => {
        if (e.key === 'Enter' && e.ctrlKey) {
            if (!($comment.value === '')) {
                $form.requestSubmit();
            }
        }
    });

    $form.noValidate = true;
    $form.addEventListener('submit', e => {
        e.preventDefault();

        if (!ReportValidity($form)) {
            return;
        }

        Start($submit);
        $fieldset.disabled = true;

        CreateRequest(quoteHash, quoteProofID ?? '', $comment.value, u.GetUploadIDs()).
            then(resp => {
                const userEventUploads: models.UserEventUpload[] = [];
                for (const upload of u.GetUploads()) {
                    userEventUploads.push({ upload });
                }

                prependComment({
                    id: resp.data.userEventID,
                    content: $comment.value,
                    created: DateTime.local().toISO(),
                    userEventUploads,
                });

                toast.Success('Your revision request has been submitted.');

                $comment.value = '';
                u.Clear();
            }).catch(HandleRequestError).finally(() => {
                $fieldset.disabled = false;
            }).finally(() => {
                Stop($submit);
                updateSubmitDisabled();
            });
    });

    const $comments = document.querySelector('#revisions-comments') as HTMLElement;
    const $commentTemplate = document.querySelector('#revisions-comment-template') as HTMLTemplateElement;
    const $commentAttachment = document.querySelector('#revisions-comment-thumbnail-template') as HTMLTemplateElement;

    const prependComment = (userEvent: models.UserEvent) => {
        const $card = template.Clone($commentTemplate).firstElementChild as HTMLElement;
        const $time = $card.querySelector('.revisions-comment-time') as HTMLElement;
        const $comment = $card.querySelector('.revisions-comment') as HTMLElement;
        const $attachments = $card.querySelector('.revisions-comment-attachments') as HTMLElement;
        // const $edit = $card.querySelector('.revisions-comment-edit') as HTMLAnchorElement;
        const $delete = $card.querySelector('.revisions-comment-delete') as HTMLAnchorElement;

        const created = DateTime.fromISO(userEvent.created ?? '');
        if (DateTime.now().diff(created, 'seconds').seconds === 0) {
            $time.textContent = 'Just now';
        } else {
            $time.textContent = created.toRelative();
        }
        $time.title = DateTime.fromISO(userEvent.created ?? '').toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS);
        $time.setAttribute('data-relative-to-datetime', DateTime.fromISO(userEvent.created ?? '').toISO() ?? '');
        $comment.textContent = userEvent.content ?? '';

        if (userEvent.userEventUploads?.length) {
            for (const userEventUpload of userEvent.userEventUploads) {
                const $attachment = template.Clone($commentAttachment).firstElementChild as HTMLElement;

                const url = userEventUpload.upload?.key?.startsWith('blob:') ? userEventUpload.upload?.key ?? '' : `${window.cloudFront}/${userEventUpload.upload?.key ?? ''}`;

                const $a = $attachment.querySelector('.revisions-comment-thumbnail-link') as HTMLAnchorElement;
                $a.href = url;
                $a.setAttribute('data-caption', userEventUpload.upload?.name ?? '');
                $a.setAttribute('data-fancybox', userEvent.id ?? '');

                const $img = $attachment.querySelector('img') as HTMLImageElement;
                $img.src = url;
                $img.alt = userEventUpload.upload?.name ?? '';
                $img.title = userEventUpload.upload?.name ?? '';

                $attachments.appendChild($attachment);
            }
        }

        const deleteFn = () => {
            $card.classList.add('d-none');

            DeleteRequest(quoteHash, userEvent.id ?? '').
                then(() => {
                    const undoID = `undo-${UUID()}`;
                    const t = toast.Success(`<div>Your comment has been deleted. <a href="#" id="${undoID}" class="text-reset text-decoration-underline">Undo<a></div>`);

                    document.querySelector(`#${undoID}`)?.addEventListener('click', e => {
                        e.preventDefault();
                        t?.dispose();

                        undo();
                    });

                    setTimeout(() => {
                        t?.dispose();
                        $card.remove();
                    }, 15_000);
                }).catch(err => {
                    HandleRequestError(err);
                    $card.classList.remove('d-none');
                });
        };

        const undo = () => {
            $card.classList.remove('d-none');

            RestoreRequest(quoteHash, userEvent.id ?? '').
                then(() => {
                    const undoID = `undo-${UUID()}`;
                    const t = toast.Success(`<div>Your comment has been restored. <a href="#" id="${undoID}" class="text-reset text-decoration-underline">Undo<a></div>`);

                    document.querySelector(`#${undoID}`)?.addEventListener('click', e => {
                        e.preventDefault();
                        t?.dispose();

                        deleteFn();
                    });
                }).catch(err => {
                    HandleRequestError(err);
                    $card.classList.add('d-none');
                });
        };

        $delete.addEventListener('click', e => {
            e.preventDefault();
            deleteFn();
        });

        $comments.insertBefore($card, $comments.firstElementChild);
    };

    const $proofParent = document.querySelector('#revisions-proof-parent') as HTMLElement;
    const $spotCommentTemplate = document.querySelector('#revisions-spot-comment-template') as HTMLTemplateElement;

    const clearSpotCommentsActive = () => {
        for (const $spotComment of Array.from($proofParent.querySelectorAll<HTMLElement>('.revisions-spot-comment'))) {
            $spotComment.classList.remove('revisions-spot-comment--active');
        }
    };

    // imgPointToPct translates the point info from the db, which is based on the image dimensions
    // to percent numbers so we can position the elements on the page to the client
    const imgPointToPct = (x: number, y: number): { x: number; y: number } => ({
        x: (x / $proof.naturalWidth) * 100,
        y: (y / $proof.naturalHeight) * 100,
    });

    // pctToImgPoint translates the point info from percent numbers to the image dimensions, returning pixel coordinates
    const pctToImgPoint = (x: number, y: number): { x: number; y: number } => ({
        x: x / 100 * $proof.naturalWidth,
        y: y / 100 * $proof.naturalHeight,
    });

    const appendSpotComment = (userEvent: models.UserEvent) => {
        const $spotComment = template.Clone($spotCommentTemplate).firstElementChild as HTMLElement;
        const $number = $spotComment.querySelector('.revisions-spot-comment-number') as HTMLElement;

        const $newIcon = $spotComment.querySelector('.revisions-spot-comment-icon-new') as HTMLElement;
        const $newFormContainer = $spotComment.querySelector('.revisions-spot-comment-new-form') as HTMLElement;
        const $newForm = $spotComment.querySelector('.revisions-spot-comment-new-form form') as HTMLFormElement;
        const $newFieldset = $newForm.querySelector('fieldset') as HTMLFieldSetElement;
        const $newTextarea = $newForm.querySelector('textarea') as HTMLTextAreaElement;
        const $newSubmit = $newForm.querySelector('button[type="submit"]') as HTMLButtonElement;
        const $newCancel = $newForm.querySelector('.revisions-spot-comment-new-cancel') as HTMLButtonElement;
        const $newAttachments = $newForm.querySelector('.revisions-spot-comment-attachments') as HTMLLabelElement;
        const $newThumbnails = $newForm.querySelector('.revisions-spot-comment-thumbnails') as HTMLElement;

        const pcts = imgPointToPct(userEvent.point?.x ?? 0, userEvent.point?.y ?? 0);
        $spotComment.style.left = `${pcts.x}%`;
        $spotComment.style.top = `${pcts.y}%`;

        const popper = createPopper($newIcon, $newFormContainer, {
            placement: 'right-start',
            strategy: 'absolute',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, RemToPx(.5)],
                    },
                },
                {
                    name: 'preventOverflow',
                    options: {
                        boundary: $proof,  // Define the boundary manually
                    },
                },
                {
                    name: 'flip',
                    options: {
                        fallbackPlacements: ['bottom', 'left', 'top'],
                    },
                },
            ],
        });

        if (!userEvent.id) {
            $spotComment.classList.add('revisions-spot-comment-new');

            interact($newIcon).draggable({
                listeners: {
                    move(event: DragEvent) {
                        pcts.x += (event.dx / $proof.clientWidth) * 100;
                        pcts.y += (event.dy / $proof.clientHeight) * 100;

                        $spotComment.style.left = `${Math.max(0, Math.min(pcts.x, 100))}%`;
                        $spotComment.style.top = `${Math.max(0, Math.min(pcts.y, 100))}%`;
                        void popper.update();
                    },
                },
            });

            $proof.classList.add('revisions-proof--active-new-spot-comment');

            const u = new Uploader($newAttachments, {
                thumbnails: $newThumbnails,
                thumbnailTemplate: $thumbTemplate,
                thumbnailDeleteHandler,
                disableLabelSpinner: true,
                onidschange: () => {
                    updateNewSubmitDisabled();
                },
            });

            const updateNewSubmitDisabled = () => {
                $newSubmit.disabled = !$newTextarea.value.trim() && !u.GetUploadIDs().length;
            };
            updateNewSubmitDisabled();

            $newTextarea.addEventListener('input', () => {
                $newTextarea.style.height = 'auto';
                $newTextarea.style.height = `${$newTextarea.scrollHeight}px`;

                updateNewSubmitDisabled();
            });

            const cancel = () => {
                $spotComment.remove();
                $proof.classList.remove('revisions-proof--active-new-spot-comment');
                nextSpotCommentNumber = userEvent.pointNumber || 1;
            };

            $newCancel.addEventListener('click', () => {
                cancel();
            });
            $newTextarea.addEventListener('keydown', e => {
                if (e.key === 'Enter' && e.ctrlKey) {
                    $newForm.requestSubmit();
                } else if (e.key === 'Escape') {
                    cancel();
                }
            });

            $newForm.noValidate = true;
            $newForm.addEventListener('submit', e => {
                e.preventDefault();

                if (!ReportValidity($newForm)) {
                    return;
                }
                Start($newSubmit);
                $newFieldset.disabled = true;

                // "point" is looking for the x and y relative to the *image*, not the client position
                const point = pctToImgPoint(pcts.x, pcts.y);

                CreateRequest(quoteHash, quoteProofID ?? '', $newTextarea.value, u.GetUploadIDs(), point, userEvent.pointNumber).
                    then(resp => {
                        const newUserEvent: models.UserEvent = {
                            id: resp.data.userEventID,
                            content: $newTextarea.value,
                            created: DateTime.local().toISO(),
                            point,
                            pointNumber: userEvent.pointNumber,
                        };

                        appendSpotComment(newUserEvent);

                        toast.Success('<div>Your comment has been added!</div>');

                        $spotComment.remove();
                        $proof.classList.remove('revisions-proof--active-new-spot-comment');
                    }).
                    catch(HandleRequestError).
                    finally(() => {
                        $newFieldset.disabled = false;
                        Stop($newSubmit);
                    });
            });

            setTimeout(() => {
                $newTextarea.focus();
            });
        } else {
            const $details = $spotComment.querySelector('.revisions-spot-comment-details') as HTMLElement;
            const $icon = $spotComment.querySelector('.revisions-spot-comment-icon') as HTMLElement;
            const $text = $spotComment.querySelector('.revisions-spot-comment-details-text') as HTMLElement;
            const $thumbnails = $spotComment.querySelector('.revisions-spot-comment-details-thumbnails') as HTMLElement;
            const $delete = $spotComment.querySelector('.revisions-spot-comment-delete') as HTMLAnchorElement;

            let popper: ReturnType<typeof createPopper> | undefined = undefined;
            const lazyInitPopper = () => {
                if (popper) {
                    return popper;
                }
                popper = createPopper($icon, $details, {
                    placement: 'right-start',
                    strategy: 'absolute',
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, RemToPx(.5)],
                            },
                        },
                        {
                            name: 'preventOverflow',
                            options: {
                                boundary: $proof,  // Define the boundary manually
                            },
                        },
                        {
                            name: 'flip',
                            options: {
                                fallbackPlacements: ['bottom', 'left', 'top'],
                            },
                        },
                    ],
                });
            };

            $icon.addEventListener('click', e => {
                e.stopPropagation();

                const active = $spotComment.classList.contains('revisions-spot-comment--active');

                clearSpotCommentsActive();
                if (!active) {
                    $spotComment.classList.add('revisions-spot-comment--active');
                    void lazyInitPopper()?.update();
                }
            });

            const deleteFn = () => {
                $spotComment.classList.add('d-none');

                DeleteRequest(quoteHash, userEvent.id ?? '').
                    then(() => {
                        const undoID = `undo-${UUID()}`;
                        const t = toast.Success(`<div>Your comment has been deleted. <a href="#" id="${undoID}" class="text-reset text-decoration-underline">Undo<a></div>`);

                        document.querySelector(`#${undoID}`)?.addEventListener('click', e => {
                            e.preventDefault();
                            t?.dispose();

                            undo();
                        });

                        setTimeout(() => {
                            t?.dispose();
                            $spotComment.remove();
                        }, 15_000);
                    }).catch(err => {
                        HandleRequestError(err);
                        $spotComment.classList.remove('d-none');
                    });
            };

            const undo = () => {
                $spotComment.classList.remove('d-none');

                RestoreRequest(quoteHash, userEvent.id ?? '').
                    then(() => {
                        const undoID = `undo-${UUID()}`;
                        const t = toast.Success(`<div>Your comment has been restored. <a href="#" id="${undoID}" class="text-reset text-decoration-underline">Undo<a></div>`);

                        document.querySelector(`#${undoID}`)?.addEventListener('click', e => {
                            e.preventDefault();
                            t?.dispose();

                            deleteFn();
                        });
                    }).catch(err => {
                        HandleRequestError(err);
                        $spotComment.classList.add('d-none');
                    });
            };

            $delete.addEventListener('click', e => {
                e.preventDefault();
                deleteFn();
            });

            $text.textContent = userEvent.content ?? '';

            if (userEvent.userEventUploads?.length) {
                for (const userEventUpload of userEvent.userEventUploads) {
                    const $attachment = template.Clone($commentAttachment).firstElementChild as HTMLElement;

                    const url = userEventUpload.upload?.key?.startsWith('blob:') ? userEventUpload.upload?.key ?? '' : `${window.cloudFront}/${userEventUpload.upload?.key ?? ''}`;

                    const $a = $attachment.querySelector('.revisions-comment-thumbnail-link') as HTMLAnchorElement;
                    $a.href = url;
                    $a.setAttribute('data-caption', userEventUpload.upload?.name ?? '');
                    $a.setAttribute('data-fancybox', userEvent.id ?? '');

                    const $img = $attachment.querySelector('img') as HTMLImageElement;
                    $img.src = url;
                    $img.alt = userEventUpload.upload?.name ?? '';
                    $img.title = userEventUpload.upload?.name ?? '';

                    $thumbnails.appendChild($attachment);
                }
            }
        }

        if (!userEvent.pointNumber) {
            userEvent.pointNumber = nextSpotCommentNumber++;
        }
        $number.textContent = userEvent.pointNumber.toLocaleString(); // !!!! HOW MANY CAN THERE BE???1

        $proofParent.appendChild($spotComment);
    };

    $proof.addEventListener('click', e => {
        clearSpotCommentsActive();

        if ($proof.classList.contains('revisions-proof--active-new-spot-comment')) {
            return;
        }

        const rect = $proof.getBoundingClientRect();
        let x = e.clientX - rect.left;
        let y = e.clientY - rect.top;

        x = (x / $proof.clientWidth) * $proof.naturalWidth;
        y = (y / $proof.clientHeight) * $proof.naturalHeight;

        appendSpotComment({
            point: { x, y },
        });
    });

    let nextSpotCommentNumber = 1;

    if (quoteProof.userEvents?.length) {
        // sort them by created asc (so they show newest first since we're prepending)
        quoteProof.userEvents.sort((a, b) => DateTime.fromISO(a.created ?? '').toMillis() - DateTime.fromISO(b.created ?? '').toMillis());

        for (const userEvent of quoteProof.userEvents) {
            if (userEvent.point || userEvent.pointNumber) {
                if (userEvent.pointNumber) {
                    nextSpotCommentNumber = Math.max(nextSpotCommentNumber, userEvent.pointNumber + 1);
                }

                void proofReady.then(() => appendSpotComment(userEvent));
                continue;
            }

            prependComment(userEvent);
        }
    }
}
